import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Clock, Copy, Check, ExternalLink, Tag } from "lucide-react";
import { useOffer, useOffers, useCategories } from "../api/hooks";
import { goUrl, trackClick, trackView } from "../api/client";
import { OfferCard, BrandLogo, formatDiscount } from "../components/OfferCard";
import SaveButton from "../components/SaveButton";
import { GridSkeleton, ErrorState, EmptyState } from "../components/States";
import { Seo } from "../components/Seo";

const fmtDate = (d) => {
    if (!d) return null;
    try {
        return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return null;
    }
};

const OfferDetailPage = () => {
    const { offerId } = useParams();
    const { data: offer, isLoading, isError, refetch } = useOffer(offerId);
    const { data: allOffers } = useOffers();
    const { data: categories } = useCategories();
    const [copied, setCopied] = useState(false);
    const viewed = useRef(false);

    useEffect(() => {
        if (offer && !viewed.current) {
            viewed.current = true;
            trackView(offerId);
        }
    }, [offer, offerId]);

    const handleGetDeal = () => {
        trackClick(offerId, "web");
        window.open(goUrl(offerId), "_blank", "noopener,noreferrer");
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(offer.couponCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    const category = categories?.find((c) => c.slug === offer?.category);
    const related = (allOffers || []).filter((o) => o.brandId === offer?.brandId && o.id !== offer?.id).slice(0, 4);

    return (
        <>
            <Seo
                title={offer ? `${offer.title} — SahiKhoj` : "Offer — SahiKhoj"}
                description={offer ? offer.description?.slice(0, 155) || `${offer.title} at ${offer.brand?.name} — via SahiKhoj.` : "Offer details on SahiKhoj."}
                path={`/offers/${offerId}`}
                crumbs={offer ? [
                    { name: "Home", path: "/" },
                    { name: "Offers", path: "/offers" },
                    { name: offer.title, path: `/offers/${offerId}` },
                ] : null}
            />
            <section className="bg-cream pb-24 pt-32 lg:pt-40">
                <div className="mx-auto max-w-4xl px-6 sm:px-8">
                    <Link to="/offers" data-testid="offer-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-smoke transition-colors hover:text-saffron">
                        <ArrowLeft className="h-4 w-4" />
                        All offers
                    </Link>

                    {isLoading && (
                        <div className="mt-8 animate-pulse rounded-3xl border border-ink/5 bg-white p-10 shadow-sm">
                            <div className="h-12 w-12 rounded-xl bg-ink/5" />
                            <div className="mt-6 h-7 w-3/4 rounded bg-ink/5" />
                            <div className="mt-3 h-4 w-full rounded bg-ink/5" />
                            <div className="mt-2 h-4 w-2/3 rounded bg-ink/5" />
                        </div>
                    )}
                    {isError && <ErrorState onRetry={refetch} message="This offer couldn't be loaded. It may have expired." />}
                    {!isLoading && !isError && !offer && (
                        <EmptyState title="Offer not found" message="This offer may have expired or been removed." />
                    )}

                    {offer && (
                        <article data-testid="offer-detail" className="mt-8 rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-12">
                            <div className="flex flex-wrap items-center gap-4">
                                <BrandLogo brand={offer.brand} className="h-14 w-14" />
                                <div>
                                    <div className="flex items-center gap-2">
                                        {offer.brand?.slug ? (
                                            <Link to={`/brands/${offer.brand.slug}`} data-testid="offer-brand-link" className="font-medium text-ink transition-colors hover:text-saffron">
                                                {offer.brand?.name}
                                            </Link>
                                        ) : (
                                            <span className="font-medium text-ink">{offer.brand?.name}</span>
                                        )}
                                        {offer.isVerified && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-leaf/10 px-2.5 py-0.5 text-[11px] font-medium text-leaf">
                                                <BadgeCheck className="h-3 w-3" /> Verified
                                            </span>
                                        )}
                                    </div>
                                    {category && (
                                        <Link to={`/categories/${category.slug}`} className="mt-1 inline-flex items-center gap-1 text-xs uppercase tracking-wide text-smoke transition-colors hover:text-saffron">
                                            <Tag className="h-3 w-3" /> {category.name}
                                        </Link>
                                    )}
                                </div>
                                <span className="ml-auto rounded-full bg-saffron-soft px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-wider text-saffron">
                                    {formatDiscount(offer)}
                                </span>
                            </div>

                            <h1 className="mt-7 font-display text-2xl font-semibold leading-snug tracking-tight text-ink sm:text-4xl" data-testid="offer-title">
                                {offer.title}
                            </h1>

                            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-smoke">
                                {offer.expiryDate && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock className="h-4 w-4 text-saffron" />
                                        {offer.daysLeft != null && offer.daysLeft <= 0
                                            ? `Ends today (${fmtDate(offer.expiryDate)})`
                                            : `Valid till ${fmtDate(offer.expiryDate)}`}
                                    </span>
                                )}
                                {offer.minimumPurchase != null && offer.minimumPurchase > 0 && (
                                    <span>Min. purchase ₹{offer.minimumPurchase}</span>
                                )}
                            </div>

                            {offer.description && (
                                <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-smoke">{offer.description}</p>
                            )}

                            {!offer.locked && offer.couponCode && (
                                    <div data-testid="offer-coupon-box" className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-dashed border-saffron/60 bg-saffron-soft/50 p-6">
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Coupon code</p>
                                            <p className="mt-1 font-mono text-2xl font-bold tracking-widest text-ink" data-testid="offer-coupon-code">
                                                {offer.couponCode}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={copyCode}
                                            data-testid="offer-copy-button"
                                            className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors hover:bg-india"
                                        >
                                            {copied ? <Check className="h-4 w-4 text-leaf" /> : <Copy className="h-4 w-4" />}
                                            {copied ? "Copied" : "Copy code"}
                                        </button>
                                    </div>
                            )}

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <SaveButton kind="offer" id={offer.id} showLabel className="border px-6 py-4 text-sm" />
                                <button
                                    type="button"
                                    onClick={handleGetDeal}
                                    data-testid="offer-get-deal-button"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-4 text-base font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-saffron-hover"
                                >
                                    {offer.couponCode && !offer.locked ? "Copy & Go to Store" : "Get Deal"}
                                    <ExternalLink className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="mt-3 text-xs leading-relaxed text-smoke">
                                Opens the merchant's page in a new tab. Final price, availability and terms are confirmed there.
                            </p>

                            {offer.termsAndConditions && (
                                <details className="mt-8 rounded-2xl border border-ink/5 bg-cream p-5">
                                    <summary className="cursor-pointer text-sm font-medium text-ink">Terms & conditions</summary>
                                    <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-smoke">{offer.termsAndConditions}</p>
                                </details>
                            )}
                        </article>
                    )}

                    {related.length > 0 && (
                        <section className="mt-16" data-testid="offer-related">
                            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">
                                More from {offer.brand?.name}
                            </h2>
                            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                {related.map((o) => (
                                    <OfferCard key={o.id} offer={o} />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </section>
        </>
    );
};

export default OfferDetailPage;
